import { TranslocoService } from '@jsverse/transloco';
import { ActivityMessageData, ActivityType } from 'src/app/core/api/player/schemas/activity-feed';
import {
  MessageSegmentType,
  StructuredHintMessage,
} from 'src/app/ui/notification-hint/model/structured-message';
import { NotificationHintComponent } from 'src/app/ui/notification-hint/notification-hint.component';
import { ActivityUtilities } from './activity-utilities';
import { convertToUserCurrency } from '../../currency/currency-format';
import { ThemeService } from '../../theme/theme.service';
import { JwtCurrencyData } from '../../profile/profile.service';
import { GameId } from 'src/app/model/game/game-id';

export enum HintType {
  Tip = 'tip',
  Cta = 'cta',
}

interface IHintPool {
  readonly allKeys: readonly string[];
  availableKeys: Set<string>;
}

export class PlayHintActivities {
  private readonly hintPools: Map<HintType, IHintPool>;
  private messageActivityIds = new Set<string>();
  private messageActivities: ActivityMessageData[] = [];

  constructor(
    gameId: string,
    private translocoService: TranslocoService,
    private themeService: ThemeService,
    private jwtCurrency: JwtCurrencyData,
  ) {
    const hintCount: Record<string, number> = {
      [GameId.Solitaire]: 17,
      [GameId.Blocks]: 12,
      [GameId.Blackjack]: 13,
      [GameId.TwentyOne]: 17,
      [GameId.Slots]: 19,
      [GameId.Plinko]: 17,
      [GameId.Marbles]: 0,
    };

    this.hintPools = new Map([
      [
        HintType.Tip,
        this.createPool([
          ...this.generateKeys('hint-general-', 15),
          ...this.generateKeys(`hint-${gameId}-`, hintCount[gameId] ?? 0),
        ]),
      ],
      [HintType.Cta, this.createPool(this.generateKeys('hint-cta-', 23))],
    ]);
  }

  public populateMessageActivities(messageActivities: ActivityMessageData[]): void {
    this.messageActivityIds.clear();
    this.messageActivities = [];
    this.appendMessageActivities(messageActivities);
  }

  public appendMessageActivities(newMessageActivities: ActivityMessageData[]): void {
    const uniqueNew = newMessageActivities.filter((activity) => {
      if (this.messageActivityIds.has(activity.activityId)) {
        return false;
      }
      this.messageActivityIds.add(activity.activityId);
      return true;
    });

    this.messageActivities.push(...uniqueNew);
    this.messageActivities.sort((a, b) => b.createdAt - a.createdAt);
  }

  public popFirstWinMessages(count = 3): StructuredHintMessage[] {
    const winMessages: ActivityMessageData[] = [];

    this.messageActivities = this.messageActivities.filter((activity) => {
      if (winMessages.length < count && activity.data.type === ActivityType.Win) {
        winMessages.push(activity);
        return false;
      }
      return true;
    });

    return winMessages
      .map((winMessage) => this.createStructuredMessage(winMessage))
      .filter((message): message is StructuredHintMessage => message !== undefined);
  }

  public getNextMessage(): StructuredHintMessage | undefined {
    if (this.messageActivities.length === 0) {
      return undefined;
    }

    const [removed] = this.messageActivities.splice(0, 1);
    return this.createStructuredMessage(removed);
  }

  public getRandomMessage(): StructuredHintMessage | undefined {
    if (this.messageActivities.length === 0) {
      return undefined;
    }

    const randomIndex = Math.floor(Math.random() * this.messageActivities.length);
    const [removed] = this.messageActivities.splice(randomIndex, 1);
    return this.createStructuredMessage(removed);
  }

  public getRandomHint(type: HintType): StructuredHintMessage {
    const pool = this.getPool(type);

    if (pool.availableKeys.size === 0) {
      this.reset(type);
    }

    const availableArray = Array.from(pool.availableKeys);
    const randomIndex = Math.floor(Math.random() * availableArray.length);
    const selectedKey = availableArray[randomIndex];

    pool.availableKeys.delete(selectedKey);

    if (type === HintType.Tip) {
      return NotificationHintComponent.createStructuredMessage(selectedKey, [
        [MessageSegmentType.Tip, `${this.translocoService.translate(`pages.activity-feed.tip`)}: `],
        [
          MessageSegmentType.Text,
          this.translocoService.translate(`pages.activity-feed.${selectedKey}`),
        ],
      ]);
    }

    return NotificationHintComponent.createStructuredMessage(selectedKey, [
      [
        MessageSegmentType.Text,
        this.translocoService.translate(`pages.activity-feed.${selectedKey}`),
      ],
    ]);
  }

  public reset(type: HintType): void {
    const pool = this.getPool(type);
    pool.availableKeys = new Set(pool.allKeys);
  }

  public resetAll(): void {
    this.hintPools.forEach((_, type) => this.reset(type));
  }

  public remainingCount(type: HintType): number {
    return this.getPool(type).availableKeys.size;
  }

  public totalCount(type: HintType): number {
    return this.getPool(type).allKeys.length;
  }

  private generateKeys(prefix: string, count: number): string[] {
    return Array.from({ length: count }, (_, index) => `${prefix}${index + 1}`);
  }

  private createPool(keys: string[]): IHintPool {
    return {
      allKeys: keys,
      availableKeys: new Set(keys),
    };
  }

  private getPool(type: HintType): IHintPool {
    const pool = this.hintPools.get(type);
    if (!pool) {
      throw new Error(`Unknown hint type: ${type}`);
    }
    return pool;
  }

  private createStructuredMessage(
    activity: ActivityMessageData,
  ): StructuredHintMessage | undefined {
    if (ActivityUtilities.isWinData(activity.data)) {
      const converted = convertToUserCurrency(
        this.jwtCurrency.matchCurrency,
        activity.data.amount,
        {
          userCurrency: this.jwtCurrency.userCurrency,
          usdExchangeRate: this.jwtCurrency.exchangeRate,
        },
      );
      const currencyReward = this.themeService.formatCurrencyAmount(
        converted.currency,
        converted.amount,
      );

      const wonText = this.translocoService.translate('pages.activity-feed.won-hint', {
        nick: activity.data.winnerNick,
      });

      return NotificationHintComponent.createStructuredMessage(activity.activityId, [
        [MessageSegmentType.Text, `${wonText} `],
        [MessageSegmentType.Currency, currencyReward],
        [
          MessageSegmentType.Date,
          ` ${ActivityUtilities.formatTimeInterval(new Date(), new Date(activity.createdAt))}`,
        ],
      ]);
    } else if (ActivityUtilities.isDrawData(activity.data)) {
      const drawText = this.translocoService.translate('pages.activity-feed.draw-hint', {
        playerOne: activity.data.firstPlayerNick,
        playerTwo: activity.data.secondPlayerNick,
      });

      return NotificationHintComponent.createStructuredMessage(activity.activityId, [
        [MessageSegmentType.Text, `${drawText} `],
        [
          MessageSegmentType.Date,
          ` ${ActivityUtilities.formatTimeInterval(new Date(), new Date(activity.createdAt))}`,
        ],
      ]);
    } else if (ActivityUtilities.isNewPlayerData(activity.data)) {
      const newText = this.translocoService.translate('pages.activity-feed.new-hint', {
        nick: activity.data.nick,
      });

      return NotificationHintComponent.createStructuredMessage(activity.activityId, [
        [MessageSegmentType.Text, `${newText} `],
        [
          MessageSegmentType.Date,
          ` ${ActivityUtilities.formatTimeInterval(new Date(), new Date(activity.createdAt))}`,
        ],
      ]);
    }

    return undefined;
  }
}
